import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HiOutlineChevronLeft, HiOutlineRefresh, HiOutlinePlus, HiOutlineTrash,
    HiOutlineCube, HiOutlineSearch, HiOutlineDownload, HiOutlinePrinter, HiOutlineBan, HiOutlineUpload,
    HiOutlinePencil, HiOutlineCheck, HiOutlineX
} from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import { onSocketEvent, onSocketReconnect, onAppVisible } from '../../realtime/socketClient';
import UserProfileButton from '../common/UserProfileButton';
import LogoutButton from '../common/LogoutButton';
import FrozenImportWizard from './FrozenImportWizard';
import AgentHealth from './AgentHealth';
import './FrozenPickPortal.css';

// New -> Handed Over, one tap: the freezer team has no time to step a job
// through Picking and Ready. Jobs that reached Picking/Ready before the board
// was simplified are still open, so they show under New and hand over the
// same way. Cancelled gets its own column so a cancelled card is SEEN, with
// who/when/why, instead of vanishing from the board.
const OPEN_STATUSES = ['New', 'Picking', 'Ready'];
const BOARD_COLUMNS = [
    { key: 'New', match: (s) => OPEN_STATUSES.includes(s) },
    { key: 'Handed Over', match: (s) => s === 'Handed Over' },
    { key: 'Cancelled', match: (s) => s === 'Cancelled' },
];
const isOpen = (job) => OPEN_STATUSES.includes(job.status);

// Which closed jobs (Handed Over / Cancelled) the board shows. Open jobs are
// always shown whatever their age. The presets are resolved to Qatar days on
// the server; Custom sends the picked instants.
const RANGE_PRESETS = [
    { id: '24h', label: 'Last 24 h' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7d', label: 'Last 7 days' },
    { id: 'custom', label: 'Custom…' },
];
// "Last 24 h", "Today" and "Last 7 days" run up to NOW, so a job closed after
// the board was loaded still belongs; "Yesterday" and Custom have a real end.
const OPEN_ENDED = ['24h', 'today', '7d'];
const closedAt = (job) => (job.status === 'Handed Over' ? job.handedOverAt || job.updatedAt
    : job.status === 'Cancelled' ? job.cancelledAt || job.updatedAt : null);
function inRange(job, range) {
    const at = closedAt(job);
    if (!at || !range) return true; // open jobs always
    const t = new Date(at).getTime();
    if (t < new Date(range.from).getTime()) return false;
    return OPEN_ENDED.includes(range.preset) || t <= new Date(range.to).getTime();
}

const formatTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const activeLines = (job) => (job.lines || []).filter((l) => l.lineStatus !== 'removed');

const FLASH_LABEL = {
    new: 'New job',
    amended: 'Invoice amended — check the changes',
    reopened: 'Reopened — needs picking',
    cancelled: 'Cancelled'
};

// One line on a card, with its change from the last amendment spelled out:
// NEW, CHANGED (was 3 KG), or REMOVED / do not pick. Colour alone was too
// easy to miss, and the old quantity was never shown at all.
function JobLine({ line, cancelled }) {
    const status = cancelled ? 'removed' : (line.lineStatus || 'active');
    const uomChanged = line.prevUom && line.prevUom !== line.uom;
    return (
        <li className={status !== 'active' ? `fpp-line-${status}` : ''}>
            <span className="fpp-code">{line.code}</span>
            <span>
                {line.description}
                {!cancelled && status === 'new' && <span className="fpp-tag fpp-tag-new">NEW</span>}
                {!cancelled && status === 'changed' && <span className="fpp-tag fpp-tag-changed">CHANGED</span>}
                {!cancelled && status === 'removed' && <span className="fpp-tag fpp-tag-removed">REMOVED · do not pick</span>}
            </span>
            <span className="fpp-qty">
                {line.qty} {line.uom}
                {!cancelled && status === 'changed' && line.prevQty != null && (
                    <span className="fpp-was">was {line.prevQty}{uomChanged ? ` ${line.prevUom}` : ''}</span>
                )}
            </span>
        </li>
    );
}

const FROZEN_ACTIONS = [
    { key: 'unavailable', icon: '✕', label: 'Not available', text: 'Not available in frozen store.' },
    { key: 'shop-freezer', icon: '🏪', label: 'Shop freezer', text: 'Available from shop freezer.' },
    { key: 'store-2', icon: '🧊₂', label: 'Frozen Store 2', text: 'Frozen Store 2.' },
    { key: 'replacement', icon: '⇄', label: 'Replacement available', text: 'Requested item unavailable. Alternative available: ' },
];

function ResponseEditor({ line, value, onEdit, items, readOnly }) {
    const [mention, setMention] = useState(null);
    const inputRef = useRef(null);
    const filtered = mention == null ? [] : items.filter((item) => `${item.code} ${item.description}`.toLowerCase().includes(mention.query.toLowerCase())).slice(0, 8);
    const update = (text, caret) => {
        onEdit(text);
        const before = text.slice(0, caret);
        const match = before.match(/@([^@\s]*)$/);
        setMention(match ? { start: caret - match[0].length, end: caret, query: match[1] } : null);
    };
    const choose = (item) => {
        const el = inputRef.current;
        const start = mention.start;
        const end = mention.end;
        const insertion = `${item.description || item.code} (${item.code})`;
        const next = `${value.slice(0, start)}${insertion}${value.slice(end)}`;
        onEdit(next);
        setMention(null);
        requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + insertion.length, start + insertion.length); });
    };
    return <div className="fpp-response-editor">
        <textarea ref={inputRef} aria-label={`Response for ${line.description}`} value={value} readOnly={readOnly} onChange={(e) => update(e.target.value, e.target.selectionStart)} onKeyUp={(e) => update(e.currentTarget.value, e.currentTarget.selectionStart)} rows={2} />
        {mention && <div className="fpp-mention-menu" role="listbox" aria-label="Frozen item suggestions">
            {filtered.length ? filtered.map((item) => <button key={item.code} type="button" role="option" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(item)}><strong>{item.code}</strong><span>{item.description}</span></button>) : <div className="fpp-mention-empty">No matching frozen items</div>}
        </div>}
    </div>;
}

function PickJobLine({ line, cancelled, response, onAction, onEdit, items, editable }) {
    const [armed, setArmed] = useState(null);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const status = cancelled ? 'removed' : (line.lineStatus || 'active');
    const uomChanged = line.prevUom && line.prevUom !== line.uom;
    return <>
        <li className={`${status !== 'active' ? `fpp-line-${status}` : ''}${response ? ' fpp-line-responded' : ''}`}>
            <span className="fpp-code">{line.code}</span>
            <span>{line.description}{!cancelled && status === 'new' && <span className="fpp-tag fpp-tag-new">NEW</span>}{!cancelled && status === 'changed' && <span className="fpp-tag fpp-tag-changed">CHANGED</span>}{!cancelled && status === 'removed' && <span className="fpp-tag fpp-tag-removed">REMOVED · do not pick</span>}</span>
            <span className="fpp-qty">{line.qty} {line.uom}{!cancelled && status === 'changed' && line.prevQty != null && <span className="fpp-was">was {line.prevQty}{uomChanged ? ` ${line.prevUom}` : ''}</span>}</span>
            {editable && !cancelled && status !== 'removed' && <span className="fpp-item-actions">{FROZEN_ACTIONS.map((action) => <button key={action.key} type="button" aria-label={action.label} title={`${action.label} — double-click to select`} className={`fpp-item-action${response?.action === action.key ? ' active' : ''}`} onClick={() => {
                // Double-clicking the already-selected action clears it again.
                if (armed === action.key) { clearTimeout(timer.current); setArmed(null); onAction(response?.action === action.key ? null : action); return; }
                clearTimeout(timer.current); setArmed(action.key); timer.current = setTimeout(() => setArmed(null), 800);
            }}>{action.icon}</button>)}</span>}
        </li>
        {response && <li className="fpp-response-row"><label>{line.code} response{response.action && <span className="fpp-response-action">{FROZEN_ACTIONS.find((a) => a.key === response.action)?.label || response.action}</span>}<ResponseEditor line={line} value={response.text} onEdit={onEdit} items={items} readOnly={!editable} /></label></li>}
    </>;
}

// Who/when/why for a cancelled job. Jobs cancelled before these fields existed
// have none — they could only have been cancelled by the invoice.
function CancelInfo({ job }) {
    const byInvoice = job.cancelSource !== 'manual';
    return (
        <div className="fpp-cancel-info">
            <div className="fpp-cancel-head">
                {byInvoice ? 'Cancelled by invoice edit' : `Cancelled by ${job.cancelledBy || 'unknown'}`}
                {(job.cancelledAt || job.updatedAt) && <span> · {formatTime(job.cancelledAt || job.updatedAt)}</span>}
            </div>
            <div className="fpp-cancel-reason">
                {job.cancelReason || 'All frozen items were removed from the invoice'}
            </div>
        </div>
    );
}

function CancelDialog({ job, onClose, onCancelled }) {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) { setError('Please enter a reason.'); return; }
        setSaving(true);
        setError('');
        try {
            const res = await axiosInstance.post(`/api/frozen-pick/jobs/${job.id || job._id}/cancel`, { reason: reason.trim() });
            onCancelled(res.data.job);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel the job');
            setSaving(false);
        }
    };

    return (
        <div className="fpp-modal-backdrop" onClick={() => !saving && onClose()}>
            <form className="fpp-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <h3>Cancel invoice {job.invoiceNo}?</h3>
                <p className="fpp-modal-sub">
                    {job.customerName || '—'} — pickers will be told to stop.
                </p>
                <label className="fpp-modal-label" htmlFor="fpp-cancel-reason">Reason (required)</label>
                <textarea
                    id="fpp-cancel-reason"
                    rows={3}
                    maxLength={500}
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Customer cancelled the order"
                />
                {error && <div className="fpp-modal-error">{error}</div>}
                <div className="fpp-modal-actions">
                    <button type="button" className="fpp-btn fpp-btn-ghost" onClick={onClose} disabled={saving}>Keep job</button>
                    <button type="submit" className="fpp-btn fpp-btn-danger" disabled={saving}>
                        {saving ? 'Cancelling…' : 'Cancel job'}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ------------------------------------------------------------------ Board
export function BoardTab({ canAdvance, canCancel }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    // range: what was asked for; shownRange: what the server resolved it to
    // (from/to instants), which the columns are filtered by.
    const [range, setRange] = useState({ preset: '24h' });
    const [shownRange, setShownRange] = useState(null);
    const [rangeError, setRangeError] = useState('');
    const [customDraft, setCustomDraft] = useState({ from: '', to: '' });
    const [customOpen, setCustomOpen] = useState(false);

    const load = useCallback(async () => {
        const params = range.preset === 'custom'
            ? { closedFrom: range.from, ...(range.to ? { closedTo: range.to } : {}) }
            : { closed: range.preset };
        try {
            const res = await axiosInstance.get('/api/frozen-pick/jobs', { params });
            setJobs(res.data.jobs || []);
            setShownRange(res.data.range || null);
            setRangeError('');
        } catch (err) {
            if (err.response?.status === 400) setRangeError(err.response.data?.message || 'Invalid range');
            console.error('Failed to load pick jobs', err);
        } finally {
            setLoading(false);
        }
    }, [range]);

    const pickPreset = (id) => {
        if (id === 'custom') { setCustomOpen(true); return; }
        setCustomOpen(false);
        setRange({ preset: id });
    };
    const applyCustom = (e) => {
        e.preventDefault();
        if (!customDraft.from) { setRangeError('Pick a start date and time'); return; }
        // datetime-local is the browser's local wall time; sent as an instant so
        // the UTC server cannot misread it.
        setRange({
            preset: 'custom',
            from: new Date(customDraft.from).toISOString(),
            to: customDraft.to ? new Date(customDraft.to).toISOString() : undefined,
        });
    };

    useEffect(() => { load(); }, [load]);

    // The socket event is a nudge, never the source of truth — a missed
    // 'frozen-pick:job' (brief disconnect, tab was backgrounded) must not
    // leave the board stale forever. Three layers close that gap without a
    // durable outbox/queue, which would be overkill at this scale:
    //   1. the initial load() above,
    //   2. a full re-sync on every socket (re)connect — covers "was offline,
    //      came back",
    //   3. a light periodic poll as a pure safety net for the rare case a
    //      reconnect event itself is missed.
    // The job itself is always safe in Mongo regardless (see ingestInvoice —
    // it's created/saved before any notification is even attempted), so the
    // worst case here is a stale board, never a lost job.
    // Cards the invoice just changed, highlighted for a few seconds with what
    // happened ('new' | 'amended' | 'reopened' | 'cancelled'), so a live
    // update is noticed instead of silently swapping a card's contents.
    const [flash, setFlash] = useState({});
    const flashTimers = useRef({});
    useEffect(() => () => Object.values(flashTimers.current).forEach(clearTimeout), []);

    useEffect(() => {
        const onJob = (job) => {
            const id = String(job.id || job._id);
            setJobs((prev) => {
                const idx = prev.findIndex((j) => String(j.id || j._id) === id);
                if (idx === -1) return [...prev, job];
                const next = [...prev];
                next[idx] = job;
                return next;
            });
            // changeType is set only by an invoice ingest or a cancel — a
            // picker's own status click carries none and is not highlighted.
            if (job.changeType) {
                setFlash((prev) => ({ ...prev, [id]: job.changeType }));
                clearTimeout(flashTimers.current[id]);
                flashTimers.current[id] = setTimeout(() => {
                    setFlash((prev) => { const next = { ...prev }; delete next[id]; return next; });
                }, 10000);
            }
        };

        // onSocketEvent/onSocketReconnect survive the socket being REPLACED on
        // token rotation; attaching to getSocket() directly (as this used to)
        // went deaf after the first 12-minute refresh, leaving only the poll.
        const offJob = onSocketEvent('frozen-pick:job', onJob);
        const offReconnect = onSocketReconnect(load);
        const offVisible = onAppVisible(load);
        const pollTimer = setInterval(load, 45000);

        return () => {
            offJob();
            offReconnect();
            offVisible();
            clearInterval(pollTimer);
        };
    }, [load]);

    const replaceJobs = (updated) => {
        const byId = new Map(updated.map((u) => [String(u.id || u._id), u]));
        setJobs((prev) => prev.map((j) => byId.get(String(j.id || j._id)) || j));
    };

    // Per-card guard so a double tap sends one request.
    const [handing, setHanding] = useState({});
    const [itemResponses, setItemResponses] = useState({});
    const [frozenItems, setFrozenItems] = useState([]);
    useEffect(() => { axiosInstance.get('/api/frozen-pick/items').then((res) => setFrozenItems(res.data.items || [])).catch((err) => console.error('Failed to load frozen item suggestions', err)); }, []);
    const handOver = async (job) => {
        const id = String(job.id || job._id);
        if (handing[id]) return;
        setHanding((prev) => ({ ...prev, [id]: true }));
        try {
            const responses = itemResponses[id] || {};
            const itemActions = Object.entries(responses).map(([lineIndex, value]) => ({ lineIndex: Number(lineIndex), action: value.action, response: value.text }));
            const res = await axiosInstance.post(`/api/frozen-pick/jobs/${id}/status`, { status: 'Handed Over', itemActions });
            replaceJobs([res.data.job]);
            setItemResponses((prev) => { const next = { ...prev }; delete next[id]; return next; });
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to mark as handed over');
            // 409 = the job changed under this tab (e.g. cancelled) — show the truth.
            if (err.response?.status === 409) load();
        } finally {
            setHanding((prev) => { const next = { ...prev }; delete next[id]; return next; });
        }
    };

    // Hands over exactly the cards on screen — never "everything open", so a
    // job that arrives while the confirm is up is not handed over unseen.
    const [handingAll, setHandingAll] = useState(false);
    const handOverAll = async (openJobs) => {
        if (!openJobs.length || handingAll) return;
        if (!window.confirm(`Mark all ${openJobs.length} job(s) in New as Handed Over?`)) return;
        setHandingAll(true);
        try {
            const res = await axiosInstance.post('/api/frozen-pick/jobs/handover', { ids: openJobs.map((j) => String(j.id || j._id)) });
            replaceJobs(res.data.jobs || []);
            if (res.data.skipped) {
                alert(`${res.data.handedOver} handed over. ${res.data.skipped} had changed meanwhile (cancelled or already handed over) — the board is refreshed.`);
                load();
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to hand over the jobs');
            load();
        } finally {
            setHandingAll(false);
        }
    };

    const [cancelTarget, setCancelTarget] = useState(null);
    const [handedOverSearch, setHandedOverSearch] = useState('');
    const onCancelled = (updated) => {
        setJobs((prev) => prev.map((j) => ((j.id || j._id) === (updated.id || updated._id) ? updated : j)));
        setCancelTarget(null);
    };

    // Per-card print state: 'sending' disables the button so a double-click
    // cannot send two reprints; 'sent' shows a brief confirmation. There is no
    // print queue, so an offline agent is reported, never shown as success.
    const [printState, setPrintState] = useState({});
    const setCardPrint = (id, state) => setPrintState((prev) => ({ ...prev, [id]: state }));

    const reprint = async (job) => {
        const id = job.id || job._id;
        if (printState[id] === 'sending') return;
        setCardPrint(id, 'sending');
        try {
            const res = await axiosInstance.post(`/api/frozen-pick/jobs/${id}/print`);
            if (!res.data.printAgentOnline) {
                setCardPrint(id, null);
                alert('No print agent is connected — the slip was NOT printed. Check the operator PC, then try again.');
                return;
            }
            setCardPrint(id, 'sent');
            setTimeout(() => setCardPrint(id, null), 3000);
        } catch (err) {
            setCardPrint(id, null);
            alert(err.response?.data?.message || 'Failed to send the slip to the printer');
        }
    };

    // Live updates can bring in a job closed outside the chosen range (e.g. one
    // handed over now while "Yesterday" is shown) — the range decides.
    const shownJobs = jobs.filter((j) => inRange(j, shownRange));
    const handedOverQuery = handedOverSearch.trim().toLocaleLowerCase();
    const columns = BOARD_COLUMNS.map((col) => ({
        status: col.key,
        jobs: shownJobs.filter((j) => col.match(j.status)
            && (col.key !== 'Handed Over' || !handedOverQuery || [j.invoiceNo, j.jobNumber, j.jobNo, j.customerName]
                .some((value) => String(value || '').toLocaleLowerCase().includes(handedOverQuery))))
            .sort((a, b) => (col.key === 'New'
            // oldest first — that is the one waiting longest
            ? new Date(a.createdAt) - new Date(b.createdAt)
            // most recent on top — that is the one someone is looking for
            : new Date(b.cancelledAt || b.updatedAt) - new Date(a.cancelledAt || a.updatedAt)))
    }));

    if (loading) return <div className="fpp-empty">Loading pick board…</div>;

    const rangeLabel = shownRange && range.preset === 'custom'
        ? `${formatTime(shownRange.from)} – ${formatTime(shownRange.to)}` : null;

    return (
        <>
        <div className="fpp-range-bar" role="group" aria-label="Closed jobs time range">
            <span className="fpp-range-title">Handed over / cancelled:</span>
            {RANGE_PRESETS.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    className={`fpp-chip${(p.id === 'custom' ? customOpen || range.preset === 'custom' : range.preset === p.id && !customOpen) ? ' active' : ''}`}
                    onClick={() => pickPreset(p.id)}
                >
                    {p.label}
                </button>
            ))}
            {rangeLabel && !customOpen && <span className="fpp-muted fpp-small">{rangeLabel}</span>}
            {customOpen && (
                <form className="fpp-range-custom" onSubmit={applyCustom}>
                    <input type="datetime-local" aria-label="From" value={customDraft.from}
                        onChange={(e) => setCustomDraft((d) => ({ ...d, from: e.target.value }))} required />
                    <span>to</span>
                    <input type="datetime-local" aria-label="To" value={customDraft.to}
                        onChange={(e) => setCustomDraft((d) => ({ ...d, to: e.target.value }))} />
                    <button type="submit" className="fpp-btn">Show</button>
                </form>
            )}
            {rangeError && <span className="fpp-refresh-error" role="alert">{rangeError}</span>}
        </div>
        <div className="fpp-board">
            {columns.map((col) => (
                <div key={col.status} role="region" aria-label={col.status} className={`fpp-column${col.status === 'Cancelled' ? ' fpp-column-cancelled' : ''}`}>
                    <div className="fpp-column-header">
                        <span>{col.status}</span>
                        <span className="fpp-column-tools">
                            {col.status === 'New' && canAdvance && col.jobs.length > 1 && (
                                <button
                                    className="fpp-btn fpp-handover-all"
                                    disabled={handingAll}
                                    onClick={() => handOverAll(col.jobs)}
                                    title="Mark every job in this column as Handed Over"
                                >
                                    <HiOutlineCheck /> {handingAll ? 'Handing over…' : 'Hand over all'}
                                </button>
                            )}
                            <span className="fpp-count">{col.jobs.length}</span>
                        </span>
                    </div>
                    <div className="fpp-column-body">
                        {col.status === 'Handed Over' && (
                            <label className="fpp-handover-search">
                                <HiOutlineSearch aria-hidden="true" />
                                <input
                                    type="search"
                                    aria-label="Search handed over jobs"
                                    placeholder="Search job number, invoice, or customer"
                                    value={handedOverSearch}
                                    onChange={(e) => setHandedOverSearch(e.target.value)}
                                />
                                {handedOverSearch && <button type="button" aria-label="Clear handover search" onClick={() => setHandedOverSearch('')}><HiOutlineX /></button>}
                            </label>
                        )}
                        {col.jobs.length === 0 && <div className="fpp-empty-col">{col.status === 'Handed Over' && handedOverQuery ? 'No matching handovers' : 'No jobs'}</div>}
                        {col.jobs.map((job) => {
                            const id = job.id || job._id;
                            const cancelled = job.status === 'Cancelled';
                            // Every line is shown, including the ones the latest
                            // amendment REMOVED (struck through, "do not pick") —
                            // hiding them left the picker with no way to know
                            // something they may already hold is gone.
                            const lines = job.lines || [];
                            const open = isOpen(job);
                            const showCancel = canCancel && open;
                            const flashed = flash[String(id)];
                            return (
                                <div key={id} role="article" aria-label={job.invoiceNo} className={`fpp-card${cancelled ? ' fpp-cancelled' : job.amended ? ' fpp-amended' : ''}${flashed ? ' fpp-flash' : ''}`}>
                                    {flashed && <div className={`fpp-flash-banner fpp-flash-${flashed}`}>{FLASH_LABEL[flashed] || 'Updated'}</div>}
                                    <div className="fpp-card-top">
                                        <strong>{job.invoiceNo}</strong>
                                        <span className="fpp-time">{formatTime(job.createdAt)}</span>
                                    </div>
                                    <div className="fpp-customer">
                                        {job.customerName || '—'}
                                        {job.amended && !cancelled && <span className="fpp-tag fpp-tag-amended">Amended · rev {job.revision}</span>}
                                    </div>
                                    {cancelled && <CancelInfo job={job} />}
                                    <ul className="fpp-lines">
                                        {lines.map((l, i) => { const saved = (job.itemResponses || []).find((r) => r.lineIndex === i); const response = itemResponses[String(id)]?.[i] || (saved && { action: saved.action, text: saved.response }); return <PickJobLine key={i} line={l} cancelled={cancelled} editable={open && canAdvance} items={frozenItems} response={response} onAction={(action) => setItemResponses((prev) => { const forJob = { ...(prev[id] || {}) }; if (action) forJob[i] = { action: action.key, text: action.text }; else delete forJob[i]; return { ...prev, [id]: forJob }; })} onEdit={(text) => setItemResponses((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [i]: { ...prev[id][i], text } } }))} />; })}
                                    </ul>
                                    {!cancelled && (canAdvance || showCancel) && (
                                        <div className="fpp-card-actions">
                                            {canAdvance && open && (
                                                <button
                                                    className="fpp-btn fpp-advance"
                                                    disabled={!!handing[String(id)]}
                                                    onClick={() => handOver(job)}
                                                >
                                                    <HiOutlineCheck /> {handing[String(id)] ? 'Saving…' : 'Handed Over'}
                                                </button>
                                            )}
                                            {canAdvance && (
                                                <button
                                                    className="fpp-btn fpp-print"
                                                    title="Reprint this slip"
                                                    disabled={printState[id] === 'sending'}
                                                    onClick={() => reprint(job)}
                                                >
                                                    <HiOutlinePrinter />
                                                    {printState[id] === 'sending' ? 'Printing…'
                                                        : printState[id] === 'sent' ? 'Sent ✓' : 'Print'}
                                                </button>
                                            )}
                                            {showCancel && (
                                                <button className="fpp-btn fpp-cancel" title="Cancel this job" onClick={() => setCancelTarget(job)}>
                                                    <HiOutlineBan /> Cancel
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
        {cancelTarget && (
            <CancelDialog job={cancelTarget} onClose={() => setCancelTarget(null)} onCancelled={onCancelled} />
        )}
        </>
    );
}

// ------------------------------------------------------------ Agent Health
// Its own tab: as a panel above the item list it pushed the list down and had
// to be opened and closed. Here it opens expanded.
function AgentsTab() {
    const [heartbeats, setHeartbeats] = useState([]);
    const [heartbeatsAt, setHeartbeatsAt] = useState(null);
    const [loading, setLoading] = useState(true);

    // Throws on failure so the Refresh button can say so.
    const loadHeartbeats = useCallback(async () => {
        const res = await axiosInstance.get('/api/frozen-pick/agent-heartbeats');
        setHeartbeats(res.data.heartbeats || []);
        setHeartbeatsAt(new Date());
    }, []);

    useEffect(() => {
        loadHeartbeats()
            .catch((err) => console.error('Failed to load agent health', err))
            .finally(() => setLoading(false));
        // Agents report every minute; "offline" only appears if the list is re-read.
        const id = setInterval(() => {
            loadHeartbeats().catch((err) => console.error('Failed to load agent health', err));
        }, 60000);
        return () => clearInterval(id);
    }, [loadHeartbeats]);

    if (loading) return <div className="fpp-empty">Loading…</div>;
    return <AgentHealth heartbeats={heartbeats} updatedAt={heartbeatsAt} onRefresh={loadHeartbeats} defaultExpanded />;
}

// ------------------------------------------------------------------ Admin
function AdminTab({ canEdit, canDelete }) {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ code: '', description: '', uom: '' });
    const [importOpen, setImportOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [itemQuery, setItemQuery] = useState('');

    const load = useCallback(async () => {
        try {
            const itemsRes = await axiosInstance.get('/api/frozen-pick/items');
            setItems(itemsRes.data.items || []);
        } catch (err) {
            console.error('Failed to load admin data', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // All items are already loaded (a few hundred), so filtering here is
    // instant and needs no server round-trip.
    const visibleItems = useMemo(() => {
        const q = itemQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => [i.code, i.description, i.uom].some((v) => String(v || '').toLowerCase().includes(q)));
    }, [items, itemQuery]);

    const addItem = async (e) => {
        e.preventDefault();
        if (!form.code.trim()) return;
        try {
            await axiosInstance.post('/api/frozen-pick/items', form);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to add the item');
            return;
        }
        setForm({ code: '', description: '', uom: '' });
        load();
    };

    const removeItem = async (code) => {
        if (!window.confirm(`Remove ${code} from the frozen item list?`)) return;
        try {
            await axiosInstance.delete(`/api/frozen-pick/items/${encodeURIComponent(code)}`);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to remove the item');
            return;
        }
        load();
    };

    // One row at a time is edited in place; `editing.original` is the code the
    // row had, since the code itself can be changed (e.g. a lost leading zero).
    const [editing, setEditing] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const startEdit = (i) => setEditing({ original: i.code, code: i.code, description: i.description || '', uom: i.uom || '' });

    const saveEdit = async () => {
        if (!editing.code.trim()) { alert('Code is required.'); return; }
        setSavingEdit(true);
        try {
            const res = await axiosInstance.put(`/api/frozen-pick/items/${encodeURIComponent(editing.original)}`, {
                code: editing.code, description: editing.description, uom: editing.uom
            });
            const saved = res.data.item;
            setItems((prev) => prev
                .map((it) => (it.code === editing.original ? saved : it))
                .sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0)));
            setEditing(null);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to save the item');
        } finally {
            setSavingEdit(false);
        }
    };

    const editKeys = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
        if (e.key === 'Escape') setEditing(null);
    };
    const showActions = canEdit || canDelete;

    if (loading) return <div className="fpp-empty">Loading…</div>;

    return (
        <>
        <div className={`fpp-admin${canEdit ? '' : ' fpp-admin-single'}`}>
            {canEdit && (
            <div className="fpp-admin-col">
                    <>
                        <h3>Add frozen item</h3>
                        <form className="fpp-form-row" onSubmit={addItem}>
                            <input placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
                            <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            <input placeholder="Unit" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} />
                            <button type="submit" className="fpp-btn"><HiOutlinePlus /> Add</button>
                        </form>

                        <h3>Bulk import</h3>
                        <p className="fpp-muted">Upload any Excel/CSV or paste from Excel, match the columns, test, then import.</p>
                        <button className="fpp-btn" onClick={() => setImportOpen(true)}><HiOutlineUpload /> Import from Excel / CSV</button>
                    </>
            </div>
            )}

            {importOpen && (
                <FrozenImportWizard existingItems={items} onClose={() => setImportOpen(false)} onImported={load} />
            )}

            <div className="fpp-admin-col">
                <h3>
                    Frozen item list ({itemQuery.trim() ? `${visibleItems.length} of ${items.length}` : items.length})
                </h3>
                <div className="fpp-item-search">
                    <HiOutlineSearch aria-hidden="true" />
                    <input
                        type="search"
                        placeholder="Search code, description or unit"
                        aria-label="Search frozen items"
                        value={itemQuery}
                        onChange={(e) => setItemQuery(e.target.value)}
                    />
                </div>
                <table className="fpp-table">
                    <thead><tr><th>Code</th><th>Description</th><th>Unit</th>{showActions && <th /> }</tr></thead>
                    <tbody>
                        {visibleItems.length === 0 && (
                            <tr><td colSpan={showActions ? 4 : 3} className="fpp-empty-cell">
                                {items.length ? `No frozen item matches "${itemQuery.trim()}"` : 'No frozen items yet'}
                            </td></tr>
                        )}
                        {visibleItems.map((i) => (editing && editing.original === i.code ? (
                            <tr key={i.code} className="fpp-edit-row">
                                <td><input aria-label="Code" value={editing.code} autoFocus onKeyDown={editKeys} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></td>
                                <td><input aria-label="Description" value={editing.description} onKeyDown={editKeys} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></td>
                                <td><input aria-label="Unit" value={editing.uom} onKeyDown={editKeys} onChange={(e) => setEditing({ ...editing, uom: e.target.value })} /></td>
                                <td className="fpp-row-actions">
                                    <button className="fpp-icon-btn fpp-icon-save" title="Save" aria-label="Save" disabled={savingEdit} onClick={saveEdit}><HiOutlineCheck /></button>
                                    <button className="fpp-icon-btn fpp-icon-muted" title="Cancel" aria-label="Cancel edit" disabled={savingEdit} onClick={() => setEditing(null)}><HiOutlineX /></button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={i.code}>
                                <td>{i.code}</td>
                                <td>{i.description}</td>
                                <td>{i.uom}</td>
                                {showActions && (
                                    <td className="fpp-row-actions">
                                        {canEdit && <button className="fpp-icon-btn fpp-icon-muted" title="Edit" aria-label={`Edit ${i.code}`} onClick={() => startEdit(i)}><HiOutlinePencil /></button>}
                                        {canDelete && <button className="fpp-icon-btn" title="Remove" aria-label={`Remove ${i.code}`} onClick={() => removeItem(i.code)}><HiOutlineTrash /></button>}
                                    </td>
                                )}
                            </tr>
                        )))}
                    </tbody>
                </table>
            </div>
        </div>
        </>
    );
}

// ----------------------------------------------------------------- Lookup
function LookupTab() {
    const [invoiceNo, setInvoiceNo] = useState('');
    const [result, setResult] = useState(null);
    const [searching, setSearching] = useState(false);

    const search = async (e) => {
        e.preventDefault();
        if (!invoiceNo.trim()) return;
        setSearching(true);
        try {
            const res = await axiosInstance.get(`/api/frozen-pick/lookup/${encodeURIComponent(invoiceNo.trim())}`);
            setResult(res.data);
        } catch (err) {
            setResult({ error: err.response?.data?.message || 'Lookup failed' });
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="fpp-lookup">
            <form className="fpp-form-row" onSubmit={search}>
                <input placeholder="Invoice number" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
                <button className="fpp-btn" type="submit"><HiOutlineSearch /> {searching ? 'Checking…' : 'Check'}</button>
            </form>

            {result && (
                <div className="fpp-lookup-result">
                    {result.error && <div className="fpp-lookup-error">{result.error}</div>}
                    {!result.error && !result.found && <div className="fpp-lookup-error">Invoice not seen yet.</div>}
                    {!result.error && result.found && !result.hasFrozenItems && (
                        <div className="fpp-lookup-ok">No frozen items on this invoice — nothing to wait for.</div>
                    )}
                    {!result.error && result.found && result.hasFrozenItems && result.job && (
                        <div className="fpp-lookup-job">
                            <div><strong>Status:</strong> {result.job.status}</div>
                            {result.job.status === 'Cancelled' && <CancelInfo job={result.job} />}
                            <div><strong>Customer:</strong> {result.job.customerName || '—'}</div>
                            <ul className="fpp-lines">
                                {activeLines(result.job).map((l, i) => (
                                    <li key={i}><span className="fpp-code">{l.code}</span><span>{l.description}</span><span className="fpp-qty">{l.qty} {l.uom}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ----------------------------------------------------------------- Report
// A datetime-local input holds LOCAL wall-clock text ("YYYY-MM-DDTHH:mm").
// toISOString() would give the UTC text instead (Qatar midnight -> "21:00"
// the day before), which the input then misreads as local — shifting the
// whole default range 3h early. new Date(value).toISOString() on submit is
// what converts local -> UTC for the API.
function toLocalInputValue(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function ReportTab() {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const [from, setFrom] = useState(toLocalInputValue(startOfDay));
    const [to, setTo] = useState(toLocalInputValue(endOfDay));
    const [report, setReport] = useState(null);

    const run = async (e) => {
        e?.preventDefault();
        const res = await axiosInstance.get('/api/frozen-pick/report/daily', {
            params: { from: new Date(from).toISOString(), to: new Date(to).toISOString() }
        });
        setReport(res.data);
    };

    useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const [downloading, setDownloading] = useState(null);

    // A plain <a href> would navigate the browser directly to the API, which
    // sends no Authorization header (that's only injected by axiosInstance's
    // interceptor) and 401s — even inside the app, and always for a copied/
    // shared link. Fetch as an authenticated blob instead, then hand the
    // browser a local object URL to save.
    const download = async (kind) => {
        setDownloading(kind);
        try {
            const res = await axiosInstance.get(`/api/frozen-pick/report/${kind}`, {
                params: { from: new Date(from).toISOString(), to: new Date(to).toISOString() },
                responseType: 'blob'
            });
            const disposition = res.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : kind;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to download the report');
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="fpp-report">
            <form className="fpp-form-row" onSubmit={run}>
                <label>From <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                <label>To <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                <button className="fpp-btn" type="submit"><HiOutlineRefresh /> Run</button>
            </form>

            {report && (
                <>
                    <div className="fpp-summary">
                        <div><strong>{report.summary.totalJobs}</strong><span>Invoices with frozen items</span></div>
                        <div><strong>{report.summary.handedOverCount}</strong><span>Handed over</span></div>
                        <div><strong>{report.summary.cancelledCount}</strong><span>Cancelled</span></div>
                        <div><strong>{report.summary.avgMinutesToHandover ?? '—'}</strong><span>Avg minutes to hand over</span></div>
                    </div>
                    <div className="fpp-form-row">
                        <button className="fpp-btn" disabled={downloading === 'daily.xlsx'} onClick={() => download('daily.xlsx')}>
                            <HiOutlineDownload /> {downloading === 'daily.xlsx' ? 'Downloading…' : 'Daily report (.xlsx)'}
                        </button>
                        <button className="fpp-btn" disabled={downloading === 'handover.xlsx'} onClick={() => download('handover.xlsx')}>
                            <HiOutlineDownload /> {downloading === 'handover.xlsx' ? 'Downloading…' : 'Handover list (.xlsx)'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

// ------------------------------------------------------------------ Shell
const FrozenPickPortal = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const permissions = user?.permissions;
    const [tab, setTab] = useState('board');

    // system_admin bypasses requireCard on the backend entirely, but doesn't
    // necessarily have 'frozen-pick-portal' populated in permissions.cards —
    // that field is only set when someone explicitly grants the card. Every
    // other module in this app ORs in the role check for exactly this reason
    // (see AgingReports.js, ChequeRegister.js, etc.) — without it an admin
    // who hasn't been granted the card by name sees the page (ProtectedRoute
    // also allows system_admin through) but every button stays hidden.
    const isSystemAdmin = user?.role === 'system_admin';
    const canAdvance = isSystemAdmin || hasCardAction(permissions, 'frozen-pick-portal', 'create');
    const canEdit = isSystemAdmin || hasCardAction(permissions, 'frozen-pick-portal', 'edit');
    const canDelete = isSystemAdmin || hasCardAction(permissions, 'frozen-pick-portal', 'delete');
    // A plain view-only holder of the card is a picker who only needs the
    // board — Admin/Lookup/Reports are for whoever also has at least one
    // write action (maintaining the list, or system_admin). `canAdvance`
    // counts too: someone who can only advance a job's status is still a
    // picker, not a manager, but is a step above a pure viewer.
    const hasAnyWriteAction = isSystemAdmin || canAdvance || canEdit || canDelete;

    const tabs = useMemo(() => {
        const all = [
            { id: 'board', label: 'Pick Board' },
            { id: 'admin', label: 'Frozen List' },
            { id: 'agents', label: 'Agent Health' },
            { id: 'lookup', label: 'Billing Lookup' },
            { id: 'report', label: 'Reports' }
        ];
        return hasAnyWriteAction ? all : all.filter((t) => t.id === 'board');
    }, [hasAnyWriteAction]);

    // If permissions load after the initial render and the currently
    // selected tab is no longer visible (or was never valid for this user),
    // fall back to the one tab everyone with the card always has.
    useEffect(() => {
        if (!tabs.some((t) => t.id === tab)) setTab('board');
    }, [tabs, tab]);

    return (
        <div className="fpp-page">
            <header className="fpp-header">
                <button className="fpp-back" onClick={() => navigate('/purpose-selection')}>
                    <HiOutlineChevronLeft /> Back
                </button>
                <div className="fpp-title"><HiOutlineCube /> Frozen Picking</div>
                <div className="fpp-header-right">
                    <UserProfileButton />
                    <LogoutButton />
                </div>
            </header>

            <nav className="fpp-tabs">
                {tabs.map((t) => (
                    <button key={t.id} className={`fpp-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                        {t.label}
                    </button>
                ))}
            </nav>

            <main className="fpp-main">
                {/* Cancel = the card's delete action, matching the backend guard. */}
                {tab === 'board' && <BoardTab canAdvance={canAdvance} canCancel={canDelete} />}
                {tab === 'admin' && <AdminTab canEdit={canEdit} canDelete={canDelete} />}
                {tab === 'agents' && <AgentsTab />}
                {tab === 'lookup' && <LookupTab />}
                {tab === 'report' && <ReportTab />}
            </main>
        </div>
    );
};

export default FrozenPickPortal;
